// @ts-nocheck
import { StackedCell, DeadlineText, contactLine } from "@/components/shared/StackedCell";
import { deadlineStatus } from "@/lib/deadline-status";
import React, { useMemo, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  CheckCircle2,
  Download,
  Edit,
  FileText,
  MoreHorizontal,
  ChevronRight,
  ChevronDown,
  Calendar,
  AlertTriangle,
  Clock,
  Package,
  ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, cn } from "@/lib/utils";
import { toast } from "sonner";

import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { MrpShortageQueueTab } from "./components/MrpShortageQueueTab";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PurchaseOrderStatus } from "@/config/status-registry";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";


function getPaymentBadge(paymentStatus: string | null | undefined) {
  const status = (paymentStatus || "UNPAID").toUpperCase();
  switch (status) {
    case "PAID":
      return {
        label: "Đã TT",
        cls: "bg-emerald-50 text-emerald-700 border-emerald-300",
      };
    case "PARTIAL":
      return {
        label: "TT 1 phần",
        cls: "bg-amber-50 text-amber-700 border-amber-300",
      };
    default:
      return {
        label: "Chưa TT",
        cls: "bg-slate-50 text-slate-600 border-slate-200",
      };
  }
}
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";

/** Trạng thái đơn mua còn huỷ được (theo quy trình PURCHASE_ORDER) */
const CANCELLABLE_PO_STATUSES = ["DRAFT", "PENDING", "APPROVED", "ORDERED"];

import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import { emsTabClass } from "@/components/ui/ems-tabs";

const FILTER_FIELDS: FilterField[] = [
  { key: "poId", label: "Mã ĐMH", type: "text", placeholder: "Mã ĐMH..." },
  { key: "vendorName", label: "Nhà cung cấp", type: "text", placeholder: "Nhà cung cấp..." },
  { key: "orderDate", label: "Ngày đặt", type: "date", placeholder: "Ngày đặt..." },
  { key: "etaDate", label: "Ngày dự kiến", type: "date", placeholder: "Ngày dự kiến..." },
];

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(
  PurchaseOrderStatus,
).map(([key, cfg]) => ({
  key,
  label: cfg.label,
  color: cfg.color,
  dotColor: cfg.dotColor,
}));

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "poId", label: "Mã ĐMH" },
  { key: "vendorName", label: "Nhà cung cấp" },
  { key: "vendorCode", label: "Mã NCC" },
  {
    key: "orderDate",
    label: "Ngày đặt",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  {
    key: "expectedDate",
    label: "Ngày dự kiến",
    type: "date",
    format: (val: any, row: any) => {
      const d = val || row?.etaDate;
      return d ? new Date(d).toLocaleDateString("vi-VN") : "";
    },
  },
  {
    key: "receivedDate",
    label: "Ngày thực nhận",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  { key: "status", label: "Trạng thái" },
  { key: "totalAmount", label: "Tổng tiền ĐMH", type: "decimal" },
  // Chi tiết vật tư
  { key: "materialCode", label: "Mã VT" },
  { key: "materialName", label: "Tên VT" },
  { key: "unit", label: "ĐVT" },
  { key: "quantity", label: "SL đặt", type: "number" },
  { key: "receivedQty", label: "SL đã nhận", type: "number" },
  { key: "unitPrice", label: "Đơn giá", type: "decimal" },
  {
    key: "itemTotal",
    label: "Thành tiền VT",
    type: "decimal",
    format: (_: any, row: any) => (row?.quantity || 0) * (row?.unitPrice || 0),
  },
  { key: "productionOrderId", label: "Mã LSX" },
  { key: "notes", label: "Ghi chú" },
];

export default function PurchasingListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routerSearch = (useRouterState({ select: (s) => s.location.search }) as Record<string, any>) || {};
  const [activeMainTab, setActiveMainTab] = useState<"mrp_queue" | "purchase_orders">(
    routerSearch?.tab === "mrp_queue" ? "mrp_queue" : "purchase_orders"
  );
  const [isDateGrouped, setIsDateGrouped] = useState(true);

  const handleMainTabChange = (tab: "mrp_queue" | "purchase_orders") => {
    setActiveMainTab(tab);
    navigate({
      search: (prev: any) => ({
        ...prev,
        tab: tab === "mrp_queue" ? "mrp_queue" : undefined,
      }),
      replace: true,
    });
  };

  const { data: tabCounts } = useQuery({
    queryKey: ["purchase-orders-tab-counts"],
    queryFn: () => trpcClient.purchaseOrders.getTabCounts.query(),
  });

  const confirm = useConfirm();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();
  const { hasPermission } = useRoleAccess();

  const {
    items,
    isLoading,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "purchase-orders",
    queryFn: (params) => trpcClient.purchaseOrders.list.query(params) as any,
    extraParams: dateParams.from
      ? { orderDateFrom: dateParams.from, orderDateTo: dateParams.to }
      : {},
  });

  const rowIds = items.map((po: any) => po.id || po.poId);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    toggleSelectAll,
    allSelected,
  } = rowSelection;

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRowExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getBulkActions = (): BulkAction[] => {
    if (selectedCount === 0) return [];
    const selectedItems = items.filter((po: any) =>
      selectedIds.has(po.id || po.poId),
    );
    // Trạng thái từ máy chủ viết hoa (DRAFT); so sánh cũ "draft" làm nút Xóa / Gửi duyệt hàng loạt luôn bị khoá
    const allDraft = selectedItems.every(
      (item: any) => String(item.status || "").toUpperCase() === "DRAFT",
    );

    const actions: BulkAction[] = [];

    if (hasPermission("purchaseOrders", "delete")) {
      actions.push({
        label: "Xóa",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.purchaseOrders.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "mục",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} mục đã chọn?`,
          });
        },
        variant: "destructive",
        disabled: !allDraft,
      });
    }

    if (hasPermission("purchaseOrders", "update")) {
      actions.push({
        label: "Gửi duyệt",
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
        onClick: async () => {
          const toastId = toast.loading(`Đang cập nhật...`);
          let success = 0;
          const failed: string[] = [];
          for (const id of Array.from(selectedIds)) {
            try {
              // Cùng đường với nút Gửi duyệt ở trang xem đơn (máy chủ kiểm bước chuyển hợp lệ)
              await trpcClient.purchaseOrders.updateStatus.mutate({ id, status: "PENDING" });
              success++;
            } catch (e: any) {
              failed.push(e?.message || "Lỗi không xác định");
            }
          }
          if (success) toast.success(`Đã gửi duyệt ${success} đơn mua hàng`, { id: toastId });
          else toast.dismiss(toastId);
          if (failed.length) toast.error(`${failed.length} đơn chưa gửi duyệt được: ${failed[0]}`);

          clearSelection();
          queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
        },
        disabled: !allDraft,
      });
    }
    return actions;
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => ({
      ...item,
      count: 0, // Count will be handled by server meta if needed
    }));
  }, []);

  const flattenPosForExport = React.useCallback((posList: any[]) => {
    const data: any[] = [];
    (posList || []).forEach((po: any) => {
      const vendorName = po.vendor?.name || po.vendor?.shortName || po.vendorName || "";
      const vendorCode = po.vendor?.code || "";
      const poItems = po.items || [];
      if (poItems.length > 0) {
        poItems.forEach((it: any) => {
          data.push({
            ...po,
            id: po.poId || po.id,
            poId: po.poId || po.id,
            vendorName,
            vendorCode,
            materialCode: it.material?.code || it.materialCode || "",
            materialName: it.material?.name || it.materialName || "",
            unit: it.material?.unit || it.unit || "",
            quantity: it.quantity || 0,
            receivedQty: it.receivedQty || 0,
            unitPrice: it.unitPrice || 0,
            productionOrderId: it.productionOrderId || it.productionOrder?.lsxId || "",
            notes: it.notes || po.notes || "",
          });
        });
      } else {
        data.push({
          ...po,
          id: po.poId || po.id,
          poId: po.poId || po.id,
          vendorName,
          vendorCode,
        });
      }
    });
    return data;
  }, []);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_don_mua_hang",
    sheetName: "Đơn Mua Hàng",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.purchaseOrders.list.query(params),
    currentFilters: serverParams,
    transformData: flattenPosForExport,
    selectedIds,
    idKey: "poId",
  });

  return (
    <>
      <ListPageShell
        trashModule="purchase-orders"
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleMainTabChange("purchase_orders")}
              className={emsTabClass(activeMainTab === "purchase_orders", { size: "sm" })}
            >
              <FileText className="w-3.5 h-3.5 text-indigo-600" />
              <span>Đơn Mua Hàng</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "purchase_orders"
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-100 text-slate-500"
                )}
              >
                {tabCounts?.all ?? totalItems}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleMainTabChange("mrp_queue")}
              className={emsTabClass(activeMainTab === "mrp_queue", { size: "sm" })}
            >
              <ShoppingCart className="w-3.5 h-3.5 text-amber-600" />
              <span>Cần Mua (MRP)</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "mrp_queue"
                    ? "bg-amber-100 text-amber-800"
                    : "bg-slate-100 text-slate-500"
                )}
              >
                {tabCounts?.mrpQueue ?? 0}
              </span>
            </button>
          </div>
        }
        headerFilters={
          activeMainTab === "purchase_orders" ? (
            <PageFilterTags
              items={filterTags}
              selectedKeys={
                statusFilter
                  ? Array.isArray(statusFilter)
                    ? statusFilter
                    : [statusFilter as string]
                  : []
              }
              onSelect={onStatusChange}
            />
          ) : undefined
        }
        dateFilter={
          activeMainTab === "purchase_orders" ? (
            <div className="flex items-center gap-2">
              <DateGroupToggleButton
                isGrouped={isDateGrouped}
                onToggle={() => setIsDateGrouped(!isDateGrouped)}
              />
              <DatePeriodFilter
                period={period}
                setPeriod={setPeriod}
                customFrom={customFrom}
                setCustomFrom={setCustomFrom}
                customTo={customTo}
                setCustomTo={setCustomTo}
              />
            </div>
          ) : undefined
        }
        unitLabel={activeMainTab === "mrp_queue" ? "Mặt hàng" : "ĐMH"}
        actions={
          <div className="flex items-center gap-2">
            <ExportImportButtons
              onExport={handleExport}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "mau_don_mua_hang")}
            />
            {hasPermission("purchaseOrders", "create") && (
              <Button
                size="sm"
                onClick={() => navigate({ to: "/purchasing/create" })}
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Plus className="w-3 h-3 mr-1" /> Tạo ĐMH
              </Button>
            )}
          </div>
        }
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        currentPage={activeMainTab === "mrp_queue" ? 1 : currentPage}
        pageSize={activeMainTab === "mrp_queue" ? Math.max(100, (tabCounts?.mrpQueue || 100)) : pageSize}
        totalItems={activeMainTab === "mrp_queue" ? (tabCounts?.mrpQueue || 0) : totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={activeMainTab === "purchase_orders" && showFilter}
      >
        {activeMainTab === "mrp_queue" ? (
          <MrpShortageQueueTab />
        ) : (
          <table
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table min-w-[1300px]",
              selectionMode && "apv-select-mode",
            )}
          >
          <thead className="apv-table-header">
            <tr>
              <DataTableSelectAllCheckbox
                rowSelection={rowSelection}
                className="w-10 text-center"
              />
              <th className="w-8 px-1 py-1.5 text-center text-slate-400 font-normal">#</th>
              <SortableHeader
                label="Mã đơn hàng"
                field="poId"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[130px]"
              />
              <SortableHeader
                label="Nhà cung cấp"
                field="vendor.name"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[190px]"
              />
              <th className="min-w-[210px] px-2 py-1.5 text-left">Vật tư đặt mua</th>
              <th className="w-[140px] px-2 py-1.5 text-left">Tiến độ nhận hàng</th>
              <SortableHeader
                label="Ngày đặt"
                field="orderDate"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[95px]"
              />
              <SortableHeader
                label="Hạn giao (ETA)"
                field="etaDate"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[130px]"
              />
              <th className="w-[95px] px-2 py-1.5 text-center">Thanh toán</th>
              <SortableHeader
                label="Tổng tiền (VNĐ)"
                field="totalAmount"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[120px] text-right"
              />
              <th className="w-[60px] px-2 py-1.5 apv-col-actions"></th>
            </tr>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  CHECKBOX_COL_NULL,
                  null,
                  { key: "poId" },
                  { key: "vendorName" },
                  null,
                  null,
                  { key: "orderDate" },
                  { key: "etaDate" },
                  null,
                  null,
                  null,
                ]}
                filterFields={FILTER_FIELDS}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 11 }).map((_, j) => (
                    <td key={j} className="px-2 py-2">
                      <Skeleton className="h-3 w-14" />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <DateGroupedTable<any>
                items={items}
                isGrouped={isDateGrouped}
                getDateKey={(po) => extractDateKey(po.orderDate || po.createdAt)}
                emptyState={
                  <tr>
                    <td
                      colSpan={11}
                      className="text-center text-slate-400 py-12 text-sm"
                    >
                      Chưa có đơn mua hàng
                    </td>
                  </tr>
                }
                renderGroupHeader={(group) => {
                  const groupOrderedQty = group.items.reduce(
                    (acc, po) =>
                      acc +
                      (po.items || []).reduce(
                        (s: number, it: any) => s + (Number(it.quantity) || 0),
                        0,
                      ),
                    0,
                  );
                  const groupReceivedQty = group.items.reduce(
                    (acc, po) =>
                      acc +
                      (po.items || []).reduce(
                        (s: number, it: any) => s + (Number(it.receivedQty) || 0),
                        0,
                      ),
                    0,
                  );
                  const groupTotalAmount = group.items.reduce(
                    (acc, po) => acc + (Number(po.totalAmount) || 0),
                    0,
                  );

                  return (
                    <tr
                      key={`group-${group.dateKey}`}
                      className={cn(
                        "border-y font-semibold text-xs transition-colors",
                        group.isToday
                          ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                          : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                      )}
                    >
                      <td colSpan={5} className="py-2 px-3 text-slate-800">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                            onClick={group.toggleCollapse}
                            title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                          >
                            {group.isCollapsed ? (
                              <ChevronRight size={15} />
                            ) : (
                              <ChevronDown size={15} />
                            )}
                          </Button>
                          <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                            {group.formattedDate}
                          </span>
                          <Badge
                            variant="outline"
                            className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0"
                          >
                            {group.count} đơn PO
                          </Badge>
                          {group.isToday && (
                            <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                              HÔM NAY
                            </Badge>
                          )}
                          {group.isCollapsed && (
                            <span className="text-[11px] text-slate-500 font-normal italic">
                              (Đang thu gọn - bấm để xem chi tiết)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-left font-mono font-bold text-slate-800 whitespace-nowrap">
                        <span className="text-[10px] text-slate-600">
                          Đặt: <span className="text-slate-900">{groupOrderedQty.toLocaleString("vi-VN")}</span>
                        </span>
                        <span className="text-[10px] text-emerald-700 ml-2">
                          Nhận: <span className="text-emerald-800">{groupReceivedQty.toLocaleString("vi-VN")}</span>
                        </span>
                      </td>
                      <td colSpan={3} className="px-2 py-2 text-center text-slate-400 font-mono text-[10px]">
                        —
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-slate-900 text-[12px] tabular-nums whitespace-nowrap">
                        {formatMoney(groupTotalAmount)}
                      </td>
                      <td className="px-2 py-2 apv-col-actions"></td>
                    </tr>
                  );
                }}
                renderRow={(po) => {
                const poKey = po.id || po.poId;
                const poItems = po.items || [];
                const totalOrderedQty = poItems.reduce((acc: number, it: any) => acc + (Number(it.quantity) || 0), 0);
                const totalReceivedQty = poItems.reduce((acc: number, it: any) => acc + (Number(it.receivedQty) || 0), 0);
                const progressPercent = totalOrderedQty > 0
                  ? Math.min(100, Math.round((totalReceivedQty / totalOrderedQty) * 100))
                  : (["COMPLETED", "RECEIVED"].includes(String(po.status || "").toUpperCase()) ? 100 : 0);
                const isExpanded = expandedRows.has(poKey);
                // Tình trạng hạn giao theo ngày lịch VN; trước đây so "received" chữ thường nên đơn RECEIVED vẫn báo trễ
                const poStatus = String(po.status || "").toUpperCase();
                const etaRaw = po.etaDate || po.expectedDate;
                const receivedLate = po.receivedDate && etaRaw && new Date(po.receivedDate) > new Date(etaRaw);
                const etaStatus = deadlineStatus(etaRaw, {
                  done: ["RECEIVED", "COMPLETED", "CLOSED"].includes(poStatus),
                  doneLabel: receivedLate ? "Nhận trễ hạn" : "Đã nhận đủ",
                  cancelled: poStatus === "CANCELLED",
                  todayLabel: "Giao hôm nay",
                  overdueLabel: (d) => `Trễ ${d} ngày`,
                });
                const payBadge = getPaymentBadge(po.paymentStatus);

                return (
                  <React.Fragment key={poKey}>
                    <DataTableRow
                      id={poKey}
                      rowSelection={rowSelection}
                      className={cn(
                        "cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0",
                        selectedIds.has(poKey) && "bg-indigo-50/60",
                        isExpanded && "bg-slate-50/50",
                      )}
                      onClick={() =>
                        navigate({
                          to: "/purchasing/$poId",
                          params: { poId: poKey },
                        })
                      }
                    >
                      <DataTableRowCheckbox
                        id={poKey}
                        rowSelection={rowSelection}
                      />

                      {/* Expand Chevron */}
                      <td
                        className="w-8 px-1 py-1.5 text-center"
                        onClick={(e) => toggleRowExpand(poKey, e)}
                      >
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-slate-200 text-slate-500 transition-colors"
                          title={isExpanded ? "Thu gọn chi tiết" : "Xem chi tiết vật tư"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-indigo-600" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                          )}
                        </button>
                      </td>

                      {/* Mã Đơn & Trạng thái */}
                      <td className="apv-col-code px-2 py-1.5">
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-bold text-indigo-600 leading-tight">
                            {po.poId}
                          </span>
                          <StatusBadge
                            type="PurchaseOrderStatus"
                            value={po.status}
                            className="text-[9px] px-1.5 py-0 min-h-0 h-4 border-none"
                          />
                        </div>
                      </td>

                      {/* Nhà cung cấp */}
                      <td className="px-2 py-1.5">
                        <div className="font-semibold text-slate-800 leading-tight">
                          {po.vendor?.name || po.vendorName || "—"}
                        </div>
                        {(contactLine(po.vendor?.contactPerson, po.vendor?.phone) || po.vendor?.code) && (
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="po-row-contact">
                            {contactLine(po.vendor?.contactPerson, po.vendor?.phone) || po.vendor?.code}
                          </div>
                        )}
                      </td>

                      {/* Tóm tắt vật tư mua */}
                      <td className="px-2 py-1.5">
                        {poItems.length > 0 ? (
                          <div className="space-y-0.5">
                            <div className="font-medium text-slate-900 line-clamp-1 text-[11px]" title={poItems.map((it: any) => it.material?.name || it.materialName).join(", ")}>
                              {poItems[0].material?.name || poItems[0].materialName || "Vật tư"}
                              {poItems.length > 1 && (
                                <span className="ml-1 text-slate-400 font-normal">
                                  +{poItems.length - 1} loại khác
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                              Tổng {poItems.length} dòng ({totalOrderedQty.toLocaleString("vi-VN")} đơn vị)
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-[11px]">— Chưa có dòng vật tư —</span>
                        )}
                      </td>

                      {/* Tiến độ nhận hàng */}
                      <td className="px-2 py-1.5">
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] font-bold text-slate-600 tabular-nums">
                            <span>{totalReceivedQty.toLocaleString("vi-VN")} / {totalOrderedQty.toLocaleString("vi-VN")}</span>
                            <span className={cn(
                              progressPercent >= 100 ? "text-emerald-600" : progressPercent > 0 ? "text-amber-600" : "text-slate-400"
                            )}>
                              {progressPercent}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full transition-all duration-300",
                                progressPercent >= 100
                                  ? "bg-emerald-500"
                                  : progressPercent > 0
                                  ? "bg-amber-400"
                                  : "bg-slate-200",
                              )}
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Ngày đặt */}
                      <td className="px-2 py-1.5">
                        <StackedCell
                          primary={po.orderDate ? new Date(po.orderDate).toLocaleDateString("vi-VN") : "—"}
                          primaryClassName="font-mono text-[11px] text-slate-600"
                          secondary={po.creator?.fullName}
                          testId="po-row-creator"
                        />
                      </td>

                      {/* Hạn giao ETA & Cảnh báo trễ hạn thông minh */}
                      <td className="px-2 py-1.5">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 font-mono text-[11px] text-slate-700">
                            <Calendar className="h-3 w-3 text-slate-400 shrink-0" />
                            <span>
                              {po.etaDate || po.expectedDate
                                ? new Date(po.etaDate || po.expectedDate).toLocaleDateString("vi-VN")
                                : "—"}
                            </span>
                          </div>
                          <span className="text-[11px]"><DeadlineText status={etaStatus} testId="po-row-eta-status" /></span>
                        </div>
                      </td>

                      {/* Trạng thái thanh toán */}
                      <td className="px-2 py-1.5 text-center">
                        {/* Chưa có phiếu chi nhà cung cấp trên giao diện → trạng thái thanh toán chưa theo dõi được; không hiện "Chưa TT" sai */}
                        {po.paymentStatus && po.paymentStatus !== "UNPAID" ? (
                          <Badge className={cn("text-[9px] px-1.5 py-0 h-4 border shadow-2xs font-medium", payBadge.cls)}>
                            {payBadge.label}
                          </Badge>
                        ) : (
                          <span className="text-slate-400" title="Chưa theo dõi thanh toán nhà cung cấp trên phần mềm">—</span>
                        )}
                      </td>

                      {/* Tổng tiền */}
                      <td className="px-2 py-1.5 text-right font-bold text-slate-900 tabular-nums">
                        {formatMoney(po.totalAmount)}
                      </td>

                      {/* Thao tác */}
                      <td
                        className="px-2 py-1.5 text-right apv-col-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-xs">
                              Thao tác
                            </DropdownMenuLabel>
                            {hasPermission("purchaseOrders", "update") && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({
                                    to: "/purchasing/$poId/edit",
                                    params: { poId: poKey },
                                  })
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" /> Chỉnh sửa
                              </DropdownMenuItem>
                            )}
                            {hasPermission("purchaseOrders", "update") && (po.status === "ORDERED" || po.status === "PARTIAL_RECEIVED") && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({
                                    to: "/qc-materials/create" as any,
                                    // Điền sẵn NCC; đơn một dòng điền luôn vật tư, ĐVT và SL còn phải kiểm (trước: chỉ có tên NCC, lô 1000)
                                    search: (() => {
                                      const items = po.items || [];
                                      const one = items.length === 1 ? items[0] : null;
                                      const ordered = one ? Number(one.quantity) || 0 : 0;
                                      const pending = one ? Math.max(0, ordered - (Number(one.receivedQty) || 0)) : 0;
                                      return {
                                        purchaseOrderId: po.id || poKey,
                                        poCode: po.poId,
                                        vendorId: po.vendorId || po.vendor?.id,
                                        vendorName: po.vendor?.name,
                                        ...(one ? {
                                          materialId: one.materialId || one.material?.id,
                                          materialName: one.material?.name || one.materialName,
                                          unit: one.material?.unit || one.unit,
                                          orderedQty: ordered,
                                          pendingQty: pending || ordered,
                                        } : {}),
                                      };
                                    })() as any,
                                  })
                                }
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Nhận hàng (IQC)
                              </DropdownMenuItem>
                            )}
                            {/* In đơn PO ở trang chi tiết (nút In) */}
                            <DropdownMenuItem onClick={() => navigate({ to: "/purchasing/$poId", params: { poId: poKey } })}>
                              <FileText className="w-4 h-4 mr-2" /> In đơn PO
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {hasPermission("purchaseOrders", "delete") &&
                              CANCELLABLE_PO_STATUSES.includes(String(po.status || "").toUpperCase()) && (
                              <DropdownMenuItem
                                className="text-rose-600"
                                onClick={async () => {
                                  // Huỷ đơn theo quy trình đơn mua (được huỷ tới bước Đã đặt hàng) — FB-M5KUH2
                                  if (!(await confirm(`Huỷ đơn mua ${po.poId || ""}?`))) return;
                                  try {
                                    await trpcClient.purchaseOrders.updateStatus.mutate({ id: po.id, status: "CANCELLED" } as any);
                                    toast.success(`Đã huỷ đơn mua ${po.poId || ""}`);
                                    queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
                                  } catch (err: any) {
                                    toast.error(err?.message || "Không huỷ được đơn mua");
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Hủy đơn hàng
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </DataTableRow>

                    {/* Expandable Child Row: Chi tiết từng dòng vật tư */}
                    {isExpanded && (
                      <tr className="bg-slate-50/80 border-b border-slate-200">
                        <td colSpan={11} className="p-2.5 pl-10">
                          <div className="bg-white rounded-md border border-slate-200 shadow-2xs overflow-hidden">
                            <div className="px-3 py-1.5 bg-slate-100/70 border-b border-slate-200 flex items-center justify-between text-[11px] font-semibold text-slate-700">
                              <span className="flex items-center gap-1.5">
                                <Package className="h-3.5 w-3.5 text-indigo-600" />
                                Chi tiết {poItems.length} vật tư đặt mua ({po.poId})
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                Đặt: {totalOrderedQty.toLocaleString("vi-VN")} | Nhận: {totalReceivedQty.toLocaleString("vi-VN")}
                              </span>
                            </div>
                            {poItems.length === 0 ? (
                              <div className="text-center py-3 text-slate-400 text-[11px] italic">
                                Chưa có dòng vật tư nào được tạo trong đơn hàng này
                              </div>
                            ) : (
                              <table className="w-full text-[11px]">
                                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                                  <tr>
                                    <th className="px-2.5 py-1 text-left">Mã VT</th>
                                    <th className="px-2.5 py-1 text-left">Tên vật tư</th>
                                    <th className="px-2.5 py-1 text-center">ĐVT</th>
                                    <th className="px-2.5 py-1 text-right">SL đặt</th>
                                    <th className="px-2.5 py-1 text-right">Đã nhận</th>
                                    <th className="px-2.5 py-1 text-right">Còn thiếu</th>
                                    <th className="px-2.5 py-1 text-right">Đơn giá</th>
                                    <th className="px-2.5 py-1 text-right">Thành tiền</th>
                                    <th className="px-2.5 py-1 text-center">LSX</th>
                                    <th className="px-2.5 py-1 text-left">Ghi chú</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                  {poItems.map((it: any, idx: number) => {
                                    const missing = Math.max(0, (it.quantity || 0) - (it.receivedQty || 0));
                                    const itemTotal = (it.quantity || 0) * (it.unitPrice || 0);
                                    return (
                                      <tr key={it.id || idx} className="hover:bg-indigo-50/20">
                                        <td className="px-2.5 py-1 font-mono font-medium text-slate-800">
                                          {it.material?.materialId || it.material?.code || it.materialCode || "—"}
                                        </td>
                                        <td className="px-2.5 py-1 font-medium text-slate-900">
                                          {it.material?.name || it.materialName || "—"}
                                        </td>
                                        <td className="px-2.5 py-1 text-center text-slate-500">
                                          {it.material?.unit || it.unit || "—"}
                                        </td>
                                        <td className="px-2.5 py-1 text-right font-bold text-slate-800 tabular-nums">
                                          {(it.quantity || 0).toLocaleString("vi-VN")}
                                        </td>
                                        <td className="px-2.5 py-1 text-right font-bold text-emerald-600 tabular-nums">
                                          {(it.receivedQty || 0).toLocaleString("vi-VN")}
                                        </td>
                                        <td className="px-2.5 py-1 text-right tabular-nums">
                                          {missing > 0 ? (
                                            <span className="font-bold text-rose-600">
                                              {missing.toLocaleString("vi-VN")}
                                            </span>
                                          ) : (
                                            <span className="text-slate-400">0</span>
                                          )}
                                        </td>
                                        <td className="px-2.5 py-1 text-right tabular-nums text-slate-600">
                                          {formatMoney(it.unitPrice || 0)}
                                        </td>
                                        <td className="px-2.5 py-1 text-right font-bold tabular-nums text-slate-900">
                                          {formatMoney(itemTotal)}
                                        </td>
                                        <td className="px-2.5 py-1 text-center">
                                          {it.productionOrder?.orderId || it.productionOrderId ? (
                                            <Badge className="text-[9px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                              {it.productionOrder?.orderId || it.productionOrderId}
                                            </Badge>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                        <td className="px-2.5 py-1 text-slate-500 truncate max-w-[150px]">
                                          {it.notes || "—"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              }}
              renderPageSummary={(pageItems) => {
                const pageOrderedQty = pageItems.reduce(
                  (acc, po) =>
                    acc +
                    (po.items || []).reduce(
                      (s: number, it: any) => s + (Number(it.quantity) || 0),
                      0,
                    ),
                  0,
                );
                const pageReceivedQty = pageItems.reduce(
                  (acc, po) =>
                    acc +
                    (po.items || []).reduce(
                      (s: number, it: any) => s + (Number(it.receivedQty) || 0),
                      0,
                    ),
                  0,
                );
                const pageTotalAmount = pageItems.reduce(
                  (acc, po) => acc + (Number(po.totalAmount) || 0),
                  0,
                );

                return (
                  <tr
                    key="page-summary-row"
                    className="border-t-2 border-slate-300 bg-[#f8fafc] font-bold text-xs"
                  >
                    <td colSpan={5} className="py-2.5 px-3 text-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 text-xs">
                          Tổng cộng trang này:
                        </span>
                        <Badge
                          variant="secondary"
                          className="bg-slate-200 text-slate-800 text-[11px] font-bold px-2 py-0"
                        >
                          {pageItems.length} đơn PO
                        </Badge>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-left font-mono font-bold text-slate-900 whitespace-nowrap">
                      <span className="text-[11px]">
                        Đặt: {pageOrderedQty.toLocaleString("vi-VN")}
                      </span>
                      <span className="text-[11px] text-emerald-700 ml-2">
                        Nhận: {pageReceivedQty.toLocaleString("vi-VN")}
                      </span>
                    </td>
                    <td colSpan={3} className="px-2 py-2.5 text-center text-slate-400 font-mono text-[10px]">
                      —
                    </td>
                    <td className="px-2 py-2.5 text-right font-mono font-bold text-indigo-700 text-[13px] tabular-nums whitespace-nowrap">
                      {formatMoney(pageTotalAmount)}
                    </td>
                    <td className="px-2 py-2.5 apv-col-actions"></td>
                  </tr>
                );
              }}
            />
          )}
        </tbody>
        </table>
        )}
      </ListPageShell>

      {activeMainTab === "purchase_orders" && selectedCount > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
          actions={getBulkActions()}
        />
      )}
    </>
  );
}
